// Vue
import { createApp, onMounted } from "vue";

// css
import "./css/style.css";
import "./css/style-dark.css";

// Logger
import logger from "./js/logger";
import helper from "./js/helper";

// Device detector
import { createDeviceDetector } from "next-vue-device-detector";
export const device = createDeviceDetector();

// Side Panel
import VueSidePanel from "vue3-side-panel";
import "vue3-side-panel/dist/vue3-side-panel.css";

// Custom Components
import Counter from "./components/Counter.vue";
import CustomButton from "./components/CustomButton.vue";
import LoadingIndicator from "./components/LoadingIndicator.vue";
import ImageLoader from "./components/ImageLoader.vue";
import LoginRegister from "./components/LoginRegister.vue";
import ContactForm from "./components/ContactForm.vue";
import CategoryNavigation from "./components/CategoryNavigation.vue";
import AsyncLoader from "./components/AsyncLoader.vue";
import CommentsLoader from "./components/CommentsLoader.vue";
import BottomModalSheet from "./components/BottomModalSheet.vue";
import CommentsModal from "./components/CommentsModal.vue";
import ArticleShare from "./components/ArticleShare.vue";
import TranscoderProgress from "./components/TranscoderProgress.vue";
import AvatarCropper from "./components/AvatarCropper.vue";
import ProfileForm from "./components/ProfileForm.vue";
import PasswordForm from "./components/PasswordForm.vue";
import DeleteAccountForm from "./components/DeleteAccountForm.vue";
import ImpressionTracker from "./components/ImpressionTracker.vue";
import SearchModal from "./components/SearchModal.vue";
import AdminPanel from "./components/AdminPanel.vue";
import RssWidget from "./components/RssWidget.vue";
import Clipboard from "./components/Clipboard.vue";
import ThemeSwitcher from "./components/ThemeSwitcher.vue";
import PreferencesForm from "./components/PreferencesForm.vue";
import UpVote from "./components/UpVote.vue";
import Poll from "./components/Poll.vue";
import AsyncRender from "./components/AsyncRender.vue";
import CardsGenerator from "./components/CardsGenerator.vue";
import ScrollToTop from "./components/ScrollToTop.vue";
import VideoTimeline from "./components/VideoTimeline.vue";
import InfinitePicdump from "./components/InfinitePicdump.vue";
import MediaImage from "./components/MediaImage.vue"; // needed for infinite picdump component
import Uploader from "./components/Uploader.vue";
import Alert from "./components/Alert.vue";
import LazyImage from "./components/LazyImage.vue";
import ImageSwiper from "./components/ImageSwiper.vue";
import GoBack from "./components/GoBack.vue";
import HideOnScroll from "./components/HideOnScroll.vue";
import DownloadIcon from "./components/DownloadIcon.vue";
import MailingListManager from "./components/MailingListManager.vue";
import VideoPlayer from "./components/VideoPlayer.vue";
import VideoModal from "./components/VideoModal.vue";
import VideoModalListener from "./components/VideoModalListener.vue";
import CountdownRedirect from "./components/CountdownRedirect.vue";
import SocialShare from "./components/SocialShare.vue";
import AppPromotion from "./components/AppPromotion.vue";
import DonateForm from "./components/DonateForm.vue";
import SubscriptionPlans from "./components/SubscriptionPlans.vue";
import OpenPlayer from "./components/OpenPlayer.vue";

// Attach user dataset, passed via frontend on app-level
var userData = {};
var _userData = document.querySelector("#app").dataset.user;

try {
	userData = JSON.parse(_userData);
} catch (e) {
	console.error(e);
}

// Same for device data
var deviceData = {};
var _deviceData = document.querySelector("#app").dataset.device;

try {
	deviceData = JSON.parse(_deviceData);
} catch (e) {
	console.error(e);
}

// App instance
const app = createApp({
	components: {
		ImpressionTracker,
		TranscoderProgress,
		BottomModalSheet,
		CommentsModal,
		AsyncLoader,
		CommentsLoader,
		CategoryNavigation,
		Counter,
		CustomButton,
		LoadingIndicator,
		ImageLoader,
		LoginRegister,
		ContactForm,
		ArticleShare,
		AvatarCropper,
		ProfileForm,
		PasswordForm,
		DeleteAccountForm,
		SearchModal,
		AdminPanel,
		RssWidget,
		Clipboard,
		ThemeSwitcher,
		PreferencesForm,
		UpVote,
		Poll,
		AsyncRender,
		CardsGenerator,
		ScrollToTop,
		VideoTimeline,
		InfinitePicdump,
		MediaImage,
		Uploader,
		Alert,
		LazyImage,
		ImageSwiper,
		GoBack,
		HideOnScroll,
		DownloadIcon,
		MailingListManager,
		VideoPlayer,
		VideoModal,
		VideoModalListener,
		CountdownRedirect,
		SocialShare,
		AppPromotion,
		DonateForm,
		SubscriptionPlans,
		OpenPlayer,
	},
	data() {
		return {
			devMode: null,
			consent: null,
			site: {
				name: "Skoften",
				email: "info@skoften.net",
			},
			device: {
				app: deviceData.isApp,
				android: null,
				ios: null,
				mobile: null,
				desktop: null,
			},
			user: {
				loggedIn: userData.loggedIn,
				isRedactie: userData.isRedactie,
				darkTheme: userData.darkTheme,
			},
		};
	},
	methods: {
		/*
		 * Set the consent string as soon as it is available, so we can use it in other components
		 */
		setCMP() {
			var component = this;
			var hideIcon = false;

			component.$logger("app/setCMP");
			
			if (typeof __tcfapi != "undefined") {
				// Watch for user to accept
				__tcfapi("addEventListener", 2, function (tcData, success) {
					component.$logger(
						"app/setCMP/status/" + tcData.eventStatus
					);

					if (tcData.eventStatus === "useractioncomplete") {
						component.$logger(
							"app/setCMP/useractioncomplete/string: " +
								tcData.tcString
						);

						// Update app data
						component.consent = tcData.tcString;
					} else if (tcData.eventStatus === "tcloaded") {
					} else if (tcData.eventStatus === "cmpuishown") {
					}
				});

				// Set CMP consent string if available
				__tcfapi("getTCData", 2, function (result, success) {
					if (success && result && result.tcString) {
						component.$logger(
							"app/setCMP/success/" + result.tcString
						);
						component.consent = result.tcString;
					} else {
						component.$logger(
							"app/setCMP/consent-string-not-found"
						);
					}
				});
			} else {
				component.$logger("app/setCMP/__tcfapi not found");
			}
		},
		showCMP() {
			if (typeof window.__tcfapi === "function") {
				window.__tcfapi("showConsentManager", 2, function (res) {
					this.$logger("Consent Manager displayed");
				});
			} else {
				this.$logger("CMP not loaded");
			}
		},

		/*
		 * Toggle login/register modal
		 */
		loginRegister() {
			if(this.$logger) this.$logger("app/loginRegister");

			var component = this;

			if (!this.device.app) {
				UIkit.modal("#login-register-modal").show();
			} else {
				this.$helper.sendAppMessage({
					action: "toggleLogin",
				});
			}
		},

		/*
		 * Navigate url
		 */
		navigate(event) {
			const href = event.target.getAttribute("href");
			window.location = href;
		},

		/*
		 * Sticky sub navigation and article nav, toggles on scroll
		 */
		initStickyNavigation() {
			this.$logger("app/initStickyNavigation");

			var lastScrollTop = 0;
			var subBar = document.getElementById("sub-header");
			var articleFooterBar = document.getElementById(
				"article-footer-bar"
			);

			window.addEventListener(
				"scroll",
				() => {
					var currentScroll =
						window.pageYOffset ||
						document.documentElement.scrollTop;

					if (currentScroll > 100) {
						if (subBar && subBar.style) {
							subBar.style.top =
								currentScroll > lastScrollTop ? "0px" : "80px";
						}

						if (articleFooterBar && articleFooterBar.style) {
							articleFooterBar.style.bottom =
								currentScroll > lastScrollTop ? "-80px" : "0px";
						}
					}

					lastScrollTop = currentScroll <= 0 ? 0 : currentScroll; // For Mobile or negative scrolling
				},
				false
			);
		},
	},
	mounted() {
		this.$logger("app/mounted");
		this.initStickyNavigation();
	},
	created() {
		var component = this;
		
		component.$logger("app/created");
		
		// Set devMode
		component.devMode = helper.getURLParameter("dev") == "true" ? true : false;

		// Set some basic device stuff
		component.device.android = device.android;
		component.device.ios = device.ios;
		component.device.mobile = device.mobile;
		component.device.desktop = !device.mobile ? true : false;

		component.$logger(
			"app/created/device: " + JSON.stringify(component.device)
		);

		// CMP / consent string
		setTimeout(component.setCMP, 1000);

		// global variable to check if a user has interacted with the browser
		// uses for videojs
		window.userInteracted = false;
		document.addEventListener(
			"click",
			function () {
				window.userInteracted = true;
				component.$logger("app/user-interacted-with-the-dom");
			},
			{ once: true }
		);

		document.addEventListener("uikit:init", () => {});
	},
});

// Register logger & helper
app.config.globalProperties.$logger = logger;
app.config.globalProperties.$helper = helper;
app.config.globalProperties.$app = app;

// Use modules
app.use(device);
app.use(VueSidePanel);

// Mount the app
app.mount("#app");

// Expose some functions to global scope 
window.Skoften = window.Skoften || {};
window.Skoften.loginRegister = app._component.methods.loginRegister;
window.Skoften.sendAppMessage = helper.sendAppMessage;
